import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, Phone, CheckCircle, ChevronDown, Shield, Globe, Users, Zap, Brain, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fade = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const toSlug = (name) => name.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

const AI_PLATFORMS = [
  {
    category: "AI Assistants",
    desc: "The tools everyone talks about. We know how to use them for real business results.",
    tools: ["ChatGPT (OpenAI)", "Google Gemini", "Claude (Anthropic)", "Perplexity AI", "Microsoft Copilot", "Meta AI"]
  },
  {
    category: "AI for Content & Copywriting",
    desc: "Stop staring at blank pages. These tools write, edit, and optimize content at scale.",
    tools: ["Jasper AI", "Copy.ai", "Writesonic", "Grammarly AI", "Surfer SEO AI", "Frase"]
  },
  {
    category: "AI for Images & Design",
    desc: "Create visuals that used to cost thousands. In minutes, not weeks.",
    tools: ["Midjourney", "DALL-E 3 (OpenAI)", "Adobe Firefly", "Canva AI (Magic Studio)", "Stable Diffusion", "Leonardo AI"]
  },
  {
    category: "AI for Video & Audio",
    desc: "Produce professional videos and voiceovers without a production house.",
    tools: ["Sora (OpenAI)", "Runway ML", "HeyGen", "Synthesia", "Descript", "ElevenLabs", "Kling AI"]
  },
  {
    category: "AI for SEO & Search",
    desc: "Rank higher, faster. AI that understands search engines better than most agencies.",
    tools: ["Surfer SEO", "SEMrush AI", "Ahrefs AI", "MarketMuse", "Clearscope", "AlliAI"]
  },
  {
    category: "AI for Ads & Performance",
    desc: "Spend less, convert more. AI-powered ad optimization across every platform.",
    tools: ["Google Performance Max", "Meta Advantage+", "TikTok Smart Ads", "Albert AI", "Adzooma", "Smartly.io"]
  },
  {
    category: "AI for Social Media",
    desc: "Plan, create, schedule, and analyze. Social media on autopilot.",
    tools: ["Buffer AI", "Hootsuite AI", "Lately AI", "Predis AI", "Ocoya", "FeedHive"]
  },
  {
    category: "AI for Automation & CRM",
    desc: "Connect everything. Automate the boring stuff. Focus on growth.",
    tools: ["Zapier AI", "Make (Integromat)", "HubSpot AI", "Salesforce Einstein", "ActiveCampaign AI", "n8n"]
  },
  {
    category: "AI for Analytics & Data",
    desc: "Turn data into decisions. See patterns humans miss.",
    tools: ["Google Analytics 4 AI", "Tableau AI", "Looker AI", "Mixpanel AI", "Heap AI"]
  },
  {
    category: "AI for Email Marketing",
    desc: "Right message. Right person. Right time. Every time.",
    tools: ["Mailchimp AI", "Klaviyo AI", "Brevo AI", "Seventh Sense", "Phrasee"]
  }
];

const PROJECT_TYPES = [
  "AI-Powered Content Creation",
  "AI Ad Campaign Management",
  "AI Chatbot / Customer Support",
  "AI Social Media Automation",
  "AI SEO & Search Optimization",
  "AI Video & Visual Production",
  "AI Email Marketing Automation",
  "AI Analytics & Reporting",
  "AI Team Training & Workshops",
  "Full AI Marketing Transformation",
  "AI Tool Selection & Setup",
  "Custom AI Solution",
  "Not Sure Yet — Need Guidance"
];

const WHY_AI = [
  "Save time on repetitive marketing tasks",
  "Reduce costs — do more with less",
  "Competitors are already using AI",
  "Want to scale without hiring more people",
  "Improve content quality and speed",
  "Better ad performance and ROI",
  "Automate workflows and processes",
  "Stay ahead of industry changes",
  "Explore AI — not sure where to start",
  "Replace expensive tools / agencies"
];

const BUDGETS = ["Under $500", "$500 - $2,000", "$2,000 - $5,000", "$5,000 - $15,000", "$15,000+", "Not decided"];

const TEAM_MEMBERS = [
  { role: "AI Strategy Lead", location: "Dubai, UAE", exp: "12+ years in digital, 4+ in AI marketing" },
  { role: "Prompt Engineering Specialist", location: "Chandigarh, India", exp: "Built AI workflows for 50+ brands" },
  { role: "AI Automation Architect", location: "Toronto, Canada", exp: "Enterprise AI systems across 3 continents" },
  { role: "AI Content Strategist", location: "Dubai, UAE", exp: "Scaled content 10x with AI for global brands" },
  { role: "AI Performance Marketer", location: "Chandigarh, India", exp: "Manages $2M+ monthly AI-optimized ad spend" },
  { role: "AI Training & Enablement Lead", location: "Toronto, Canada", exp: "Trained 200+ marketing teams on AI adoption" }
];

export default function AIServicesPage() {
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [f, setF] = useState({
    name: "", email: "", phone: "", company: "",
    project_type: "", why_ai: "", budget: "",
    platforms_familiar: "", details: ""
  });
  const u = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone) { toast.error("Fill in your name, email, and phone."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/ai-inquiry`, f);
      setDone(true); toast.success("Submitted!"); window.scrollTo(0, 0);
    } catch { toast.error("Something went wrong. Try again."); }
    setSending(false);
  };

  if (done) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center max-w-md">
        <CheckCircle className="w-10 h-10 text-white mx-auto" />
        <h1 className="text-2xl md:text-3xl tracking-tight mt-8">We got your request.</h1>
        <p className="text-zinc-400 text-xs mt-4 tracking-wider">Our AI team will review your project and get back within 24 hours with a custom plan.</p>
        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
          <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I%20just%20submitted%20an%20AI%20project%20inquiry" target="_blank" rel="noopener noreferrer" data-testid="ai-success-whatsapp"
            className="bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center justify-center gap-3">
            Whatsapp us <ArrowRight className="w-4 h-4" />
          </a>
          <Link to="/" data-testid="ai-success-home"
            className="border border-white/20 px-8 py-4 text-xs tracking-[0.2em] hover:border-white/50 transition-colors text-center">
            Home
          </Link>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">

      {/* HERO */}
      <section data-testid="ai-hero" className="pt-32 pb-24 px-6 md:px-16 lg:px-28">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] text-zinc-500 hover:text-white transition-colors mb-10 tracking-[0.2em]" data-testid="ai-back-link">
          <ArrowLeft className="w-3 h-3" /> Home
        </Link>
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">AI Services</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1] max-w-4xl">
            AI is not replacing you.
            <br />
            <span className="text-zinc-500">But someone using AI will.</span>
          </h1>
          <p className="text-zinc-400 text-sm mt-10 max-w-2xl leading-relaxed tracking-wider">
            You've heard about ChatGPT, Midjourney, Perplexity. You know AI is changing everything. But you're not sure where to start. Or you're worried about wasting money on tools you don't understand. That's exactly why we're here.
          </p>
          <div className="flex flex-wrap gap-4 mt-12">
            <a href="#ai-form" data-testid="ai-hero-cta"
              className="bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center gap-3">
              Start your AI project <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#ai-platforms" className="border border-white/20 px-8 py-4 text-xs tracking-[0.2em] hover:border-white/50 transition-colors">
              See all AI platforms
            </a>
          </div>
        </motion.div>
      </section>

      {/* THE FEAR */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">The reality</p>
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-2xl">We understand why you're hesitant.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-16 bg-white/10">
          {[
            { icon: Shield, title: "Afraid of wasting money", desc: "AI tools have credits, subscriptions, usage limits. One wrong move and you've burned through your budget with nothing to show. We've made those mistakes so you don't have to." },
            { icon: Brain, title: "Too many options", desc: "ChatGPT or Claude? Midjourney or Firefly? Jasper or Copy.ai? Should you use Perplexity for research? The options are overwhelming. We cut through the noise and pick what actually works for your business." },
            { icon: Zap, title: "Changing too fast", desc: "New AI tools launch every week. What worked last month might be outdated. We stay on top of every update so your business always uses the best available technology." }
          ].map(item => (
            <div key={item.title} className="bg-black p-10">
              <item.icon className="w-5 h-5 text-zinc-500" />
              <p className="text-sm tracking-wider mt-6">{item.title}</p>
              <p className="text-zinc-500 text-xs mt-4 leading-relaxed tracking-wider">{item.desc}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* OUR AI TEAM */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        data-testid="ai-team-section" className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Our AI team</p>
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-2xl">Global experts. Real experience.</h2>
        <p className="text-zinc-500 text-xs mt-4 tracking-wider max-w-lg">
          Not freelancers with ChatGPT accounts. A dedicated team that has built AI systems for brands across 3 continents.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px mt-16 bg-white/10">
          {TEAM_MEMBERS.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-black p-8">
              <p className="text-xs tracking-wider">{m.role}</p>
              <p className="text-[10px] text-zinc-600 mt-2 tracking-[0.15em] flex items-center gap-1">
                <Globe className="w-2.5 h-2.5" /> {m.location}
              </p>
              <p className="text-[10px] text-zinc-500 mt-3 tracking-wider leading-relaxed">{m.exp}</p>
            </motion.div>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 pt-12 border-t border-white/10">
          {[
            { n: "200+", l: "AI projects delivered" },
            { n: "50+", l: "AI tools mastered" },
            { n: "3", l: "Continents covered" },
            { n: "85%", l: "Cost saved for clients" }
          ].map(s => (
            <div key={s.l}>
              <p className="text-2xl md:text-3xl">{s.n}</p>
              <p className="text-[10px] text-zinc-600 mt-2 tracking-[0.2em]">{s.l}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* HOW WE HELP */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">How we help</p>
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-2xl">From confused to confident. Here's how.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px mt-16 bg-white/10">
          {[
            { step: "01", title: "We listen", desc: "Tell us your business, goals, and fears. No judgment. We've heard it all." },
            { step: "02", title: "We audit", desc: "We check what AI tools fit your workflow, budget, and team skill level." },
            { step: "03", title: "We build", desc: "We set up the tools, create the workflows, train your team. You watch it work." },
            { step: "04", title: "We optimize", desc: "Monthly reviews. New tools tested. Old ones upgraded. You stay ahead. Always." }
          ].map(s => (
            <div key={s.step} className="bg-black p-10">
              <p className="text-zinc-700 text-xl">{s.step}</p>
              <p className="text-sm tracking-wider mt-4">{s.title}</p>
              <p className="text-zinc-500 text-xs mt-3 leading-relaxed tracking-wider">{s.desc}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* AI PLATFORMS */}
      <section id="ai-platforms" data-testid="ai-platforms-section" className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">AI platforms we work with</p>
          <h2 className="text-2xl md:text-3xl tracking-tight max-w-2xl">Every tool. Every platform. We know them all.</h2>
          <p className="text-zinc-500 text-xs mt-4 tracking-wider max-w-lg">
            From the biggest names to the hidden gems. We've tested, compared, and mastered them so you don't have to.
          </p>
        </motion.div>
        <div className="mt-16">
          <Accordion type="multiple" className="w-full">
            {AI_PLATFORMS.map((cat, i) => (
              <AccordionItem key={i} value={`cat-${i}`} className="border-white/10" data-testid={`ai-platform-${i}`}>
                <AccordionTrigger className="text-left text-white hover:no-underline py-6 group">
                  <div className="flex-1">
                    <p className="text-xs md:text-sm tracking-wider">{cat.category}</p>
                    <p className="text-[10px] text-zinc-600 mt-1 tracking-wider">{cat.tools.length} platforms</p>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-8">
                  <p className="text-zinc-500 text-xs tracking-wider mb-6">{cat.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {cat.tools.map(t => (
                      <Link key={t} to={`/ai-services/${toSlug(t)}`}
                        className="text-[10px] tracking-wider text-zinc-400 border border-white/10 px-4 py-2 hover:border-white/30 hover:text-white transition-colors">{t}</Link>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* USE CASES */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Who is this for</p>
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-2xl">Whether you're a one-person shop or a 500-person company.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px mt-16 bg-white/10">
          {[
            { title: "Startup founders", desc: "Use AI to do the work of a 10-person marketing team. On a 1-person budget." },
            { title: "Small business owners", desc: "Automate your social media, emails, and ads. Stop doing everything manually." },
            { title: "Marketing managers", desc: "10x your team's output. Create more content, run better campaigns, report faster." },
            { title: "Agency owners", desc: "Deliver more to clients with fewer people. AI as your competitive advantage." },
            { title: "E-commerce brands", desc: "AI product descriptions, dynamic ads, automated email flows. Scale without limits." },
            { title: "Corporate teams", desc: "Enterprise AI adoption. Train your teams. Transform your marketing operations." }
          ].map(c => (
            <div key={c.title} className="bg-black p-10">
              <Users className="w-4 h-4 text-zinc-600" />
              <p className="text-xs tracking-wider mt-4">{c.title}</p>
              <p className="text-zinc-500 text-[10px] mt-3 leading-relaxed tracking-wider">{c.desc}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* CTA FORM */}
      <section id="ai-form" data-testid="ai-form-section" className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Start your AI project</p>
          <h2 className="text-2xl md:text-3xl tracking-tight max-w-2xl">Tell us what you need. We'll build the plan.</h2>
        </motion.div>
        <form onSubmit={submit} className="mt-16 max-w-3xl space-y-12">

          {/* Basic Info */}
          <div>
            <p className="text-xs tracking-[0.2em] text-zinc-500 border-b border-white/10 pb-4 mb-6">About you</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Name *</label>
                <Input data-testid="ai-form-name" value={f.name} onChange={e => u("name", e.target.value)}
                  placeholder="Your name" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Company</label>
                <Input data-testid="ai-form-company" value={f.company} onChange={e => u("company", e.target.value)}
                  placeholder="Your company" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Email *</label>
                <Input data-testid="ai-form-email" type="email" value={f.email} onChange={e => u("email", e.target.value)}
                  placeholder="you@email.com" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Phone *</label>
                <Input data-testid="ai-form-phone" value={f.phone} onChange={e => u("phone", e.target.value)}
                  placeholder="+91 XXXXX XXXXX" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
              </div>
            </div>
          </div>

          {/* Project Type */}
          <div>
            <p className="text-xs tracking-[0.2em] text-zinc-500 border-b border-white/10 pb-4 mb-6">Your project</p>
            <div>
              <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-3">What kind of AI project?</label>
              <div className="flex flex-wrap gap-2">
                {PROJECT_TYPES.map(p => (
                  <button key={p} type="button" data-testid={`ai-project-${p.slice(0, 20)}`} onClick={() => u("project_type", p)}
                    className={`text-[10px] tracking-wider px-4 py-2 border transition-colors ${f.project_type === p ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-8">
              <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-3">Why do you want to use AI?</label>
              <div className="flex flex-wrap gap-2">
                {WHY_AI.map(w => (
                  <button key={w} type="button" data-testid={`ai-why-${w.slice(0, 15)}`} onClick={() => u("why_ai", w)}
                    className={`text-[10px] tracking-wider px-4 py-2 border transition-colors ${f.why_ai === w ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
                    {w}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Budget */}
          <div>
            <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-3">Budget for this project</label>
            <div className="flex flex-wrap gap-2">
              {BUDGETS.map(b => (
                <button key={b} type="button" data-testid={`ai-budget-${b}`} onClick={() => u("budget", b)}
                  className={`text-[10px] tracking-wider px-4 py-2 border transition-colors ${f.budget === b ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
                  {b}
                </button>
              ))}
            </div>
          </div>

          {/* Platforms */}
          <div>
            <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">AI platforms you've used or heard of</label>
            <Textarea data-testid="ai-form-platforms" value={f.platforms_familiar} onChange={e => u("platforms_familiar", e.target.value)}
              placeholder="ChatGPT, Canva AI, Jasper... or none — that's okay too" rows={2}
              className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 rounded-none text-xs tracking-wider resize-none" />
          </div>

          {/* Details */}
          <div>
            <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Tell us more about your project</label>
            <Textarea data-testid="ai-form-details" value={f.details} onChange={e => u("details", e.target.value)}
              placeholder="What's the project about? What result do you want? Any specific AI tools in mind?" rows={4}
              className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 rounded-none text-xs tracking-wider resize-none" />
          </div>

          <Button type="submit" disabled={sending} data-testid="ai-form-submit"
            className="bg-white text-black hover:bg-zinc-200 rounded-none px-10 py-4 text-xs tracking-[0.2em] h-auto disabled:opacity-50 w-full md:w-auto">
            {sending ? "Sending..." : "Submit AI project request"} {!sending && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
          <p className="text-[10px] text-zinc-600 tracking-wider">Free consultation. No commitment. We reply within 24 hours.</p>
        </form>
      </section>

      {/* SEO KEYWORDS */}
      <section className="py-16 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-[10px] text-zinc-700 tracking-[0.2em] mb-6">Related searches</p>
        <div className="flex flex-wrap gap-2">
          {[
            "AI marketing agency", "hire AI experts for marketing", "AI tools for small business",
            "ChatGPT for business marketing", "AI social media management", "AI content creation agency",
            "AI ad optimization service", "marketing automation with AI", "AI SEO services",
            "Perplexity AI for business", "Midjourney for marketing", "AI marketing consultant",
            "best AI tools for marketing 2026", "AI marketing agency Dubai", "AI marketing agency India",
            "AI marketing agency Canada", "hire remote AI team", "AI digital marketing transformation",
            "generative AI for brands", "AI-powered marketing solutions", "enterprise AI marketing",
            "AI marketing for startups", "AI marketing for ecommerce", "AI marketing ROI",
            "AI workflow automation for marketing", "train marketing team on AI",
            "hire AI marketing expert for my business", "need help using AI tools for marketing",
            "looking for agency that uses AI for advertising", "who can set up AI marketing for my company",
            "agency that can implement ChatGPT for my business", "need someone to handle AI marketing",
            "looking for AI automation specialist for marketing", "hire expert to train my team on AI tools",
            "company to help with AI-powered content creation", "need AI marketing consultant",
            "agency that can reduce my marketing costs with AI", "looking for someone to run AI ads",
            "hire AI expert for social media automation", "need help choosing the right AI tools for my business"
          ].map(kw => (
            <span key={kw} className="text-[10px] text-zinc-700 border border-white/5 px-3 py-1 tracking-wider">{kw}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
